import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { getJobTitleDetails, updateJobTitle } from '../../../../redux/Slices/JobTitlesSlice';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { applyFieldFilter } from '../../../../shared/utils.js/fieldValidation';

const EditJobTitle = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { jobTitleDetails, isLoading, error, success } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    description_en: '',
    description_ar: ''
  });
  const lastErrorRef = useRef({ message: null, time: 0 });
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    if (id) {
      dispatch(getJobTitleDetails({ id, language: i18n.language }));
    }
  }, [id, dispatch, i18n.language]);

  useEffect(() => {
    if (jobTitleDetails) {
      setFormData({
        name_en: jobTitleDetails.name_en || jobTitleDetails.name || '',
        name_ar: jobTitleDetails.name_ar || jobTitleDetails.name || '',
        description_en: jobTitleDetails.description_en || jobTitleDetails.description || '',
        description_ar: jobTitleDetails.description_ar || jobTitleDetails.description || ''
      });
    }
  }, [jobTitleDetails]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      setTimeout(() => {
        navigate('/job-titles');
        dispatch(clearSuccess());
      }, 1500);
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
      toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, navigate, dispatch, isRTL]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : applyFieldFilter(name, value);
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name_en || !formData.name_ar || !formData.description_en || !formData.description_ar) {
      toast.error(t('jobTitles.editForm.requiredFields', { defaultValue: 'جميع الحقول مطلوبة' }), { rtl: isRTL });
      return;
    }
    await dispatch(updateJobTitle({ id, jobTitleData: formData, language: i18n.language }));
  };

  const handleCancel = () => {
    navigate('/job-titles');
  };

  if (isLoading && !jobTitleDetails) {
    return (
      <div style={{ 
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('jobTitles.loading', { defaultValue: 'جاري التحميل...' })}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir,
      textAlign
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: '#1b3350',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #333'
        }}>
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #333',
            backgroundColor: '#1a1f2e'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h1 style={{ 
                margin: 0, 
                fontSize: '24px', 
                fontWeight: 'bold',
                color: 'white',
                textAlign
              }}>
                {t('jobTitles.editForm.title', { defaultValue: 'تعديل المسمى الوظيفي' })}
              </h1>
              <button
                onClick={handleCancel}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row-reverse' : 'row'
                }}
              >
                <FaArrowLeft />
                {t('jobTitles.editForm.back', { defaultValue: 'رجوع' })}
              </button>
            </div>
          </div>

          <div style={{ padding: '30px' }}>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 'bold',
                  color: 'white',
                  fontSize: '14px'
                }}>
                  {t('jobTitles.editForm.nameAr', { defaultValue: 'الاسم بالعربية *' })}
                </label>
                <input
                  type="text"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleChange}
                  placeholder={t('jobTitles.editForm.nameArPlaceholder', { defaultValue: 'أدخل الاسم بالعربية' })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    direction: dir,
                    textAlign
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 'bold',
                  color: 'white',
                  fontSize: '14px'
                }}>
                  {t('jobTitles.editForm.nameEn', { defaultValue: 'الاسم بالإنجليزية *' })}
                </label>
                <input
                  type="text"
                  name="name_en"
                  value={formData.name_en}
                  onChange={handleChange}
                  placeholder={t('jobTitles.editForm.nameEnPlaceholder', { defaultValue: 'أدخل الاسم بالإنجليزية' })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    direction: dir,
                    textAlign
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 'bold',
                  color: 'white',
                  fontSize: '14px'
                }}>
                  {t('jobTitles.editForm.descriptionAr', { defaultValue: 'الوصف بالعربية *' })}
                </label>
                <textarea
                  name="description_ar"
                  value={formData.description_ar}
                  onChange={handleChange}
                  placeholder={t('jobTitles.editForm.descriptionArPlaceholder', { defaultValue: 'أدخل الوصف بالعربية' })}
                  rows="3"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    resize: 'vertical',
                    direction: dir,
                    textAlign
                  }}
                />
              </div>

              <div style={{ marginBottom: '30px' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 'bold',
                  color: 'white',
                  fontSize: '14px'
                }}>
                  {t('jobTitles.editForm.descriptionEn', { defaultValue: 'الوصف بالإنجليزية *' })}
                </label>
                <textarea
                  name="description_en"
                  value={formData.description_en}
                  onChange={handleChange}
                  placeholder={t('jobTitles.editForm.descriptionEnPlaceholder', { defaultValue: 'أدخل الوصف بالإنجليزية' })}
                  rows="3"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    resize: 'vertical',
                    direction: dir,
                    textAlign
                  }}
                />
              </div>

              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '15px' 
              }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  {t('jobTitles.editForm.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    backgroundColor: '#AC2000',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: isLoading ? 0.7 : 1,
                    flexDirection: isRTL ? 'row-reverse' : 'row'
                  }}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      {t('jobTitles.editForm.saving', { defaultValue: 'جاري الحفظ...' })}
                    </>
                  ) : (
                    <>
                      <FaSave />
                      {t('jobTitles.editForm.save', { defaultValue: 'حفظ التغييرات' })}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditJobTitle;

